import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
    Alert,
    Box,
    Container,
    FormControl,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import DayRangePicker from '../../components/DayRangePicker';
import { EntityImage } from '../../components/EntityImage';
import { SummarySection } from './SummarySection';
import { ExplorerDetailTableHeaderLabel } from './ExplorerDetailTableHeaderLabel';
import { ExplorerPlatformIcon } from './ExplorerPlatformIcon';
import { ExplorerSocialPlacementSelect } from './ExplorerSocialPlacementSelect';
import { clipsExplorerPeriod } from './clipsExplorerPeriod';
import { ExplorerHeader } from './ExplorerHeader';
import { useExplorerDates } from './useExplorerDates';
import { useAuth } from '../../contexts/AuthContext';
import { useExplorerStore } from '../../stores/explorerStore';
import { useExplorerClipsIa } from './useExplorerClipsIa';
import apiService, { type ExplorerSocialPlatform } from '../../services/api';
import { getS3Url, ensureDirectS3Url } from '../../utils/imageUtils';
import {
    EXPLORER_TABLE_DATA_COL_WIDTH,
    GC_TIME,
    RIGHT_TABLE_SORT_LABEL_SX,
    SOCIAL_PLATFORMS,
    explorerFilterControlSx,
    explorerPaginationLabel,
    explorerRankingFixedColSx,
    explorerSocialChannelPath,
    formatExplorerTableDateLine,
    formatExplorerTableTimeLine,
    formatNumber,
    type SocialPlatform,
    type SortDirection,
} from './utils';
import { SocialBrandClipRowSkeleton } from './ExplorerLoadingSkeletons';
import { ROUTES_NAMES } from '../../components/routes';

const PLATFORM_LABEL_KEY: Record<SocialPlatform, string> = {
    facebook: 'socialFacebook',
    tiktok: 'socialTiktok',
    instagram: 'socialInstagram',
    youtubeShorts: 'socialYoutubeShorts',
};

type BrandClipSortColumn = 'publishedAt' | 'views' | 'interactions' | 'title' | 'channel';

const CLIPS_TABLE_LINK_COL_WIDTH = 64;

const CLIPS_TABLE_SX = {
    width: '100%',
    tableLayout: 'fixed' as const,
    ...explorerRankingFixedColSx(1, 168),
    ...explorerRankingFixedColSx(2, 128),
    ...explorerRankingFixedColSx(3, 108),
    ...explorerRankingFixedColSx(5, 120),
    ...explorerRankingFixedColSx(6, EXPLORER_TABLE_DATA_COL_WIDTH),
    ...explorerRankingFixedColSx(7, EXPLORER_TABLE_DATA_COL_WIDTH),
    '& > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:nth-of-type(4), & > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:nth-of-type(4)':
        { minWidth: 0 },
    '& > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:nth-of-type(8), & > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:nth-of-type(8)':
        { width: CLIPS_TABLE_LINK_COL_WIDTH, maxWidth: CLIPS_TABLE_LINK_COL_WIDTH },
};

function PlatformName({ platform, fontSize = 18 }: { platform: SocialPlatform; fontSize?: number }) {
    const { t } = useTranslation('explorer');
    const name = t(PLATFORM_LABEL_KEY[platform]);
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <ExplorerPlatformIcon platform={platform} label={name} fontSize={fontSize} />
            <Typography variant="body2" fontWeight={600} noWrap>
                {name}
            </Typography>
        </Stack>
    );
}

const ExplorerSocialBrandDetail: React.FC = () => {
    const { t } = useTranslation('explorer');
    const theme = useTheme();
    const { brandId } = useParams<{ brandId: string }>();
    const navigate = useNavigate();
    const { getDateFilterMinStartDate, getDateFilterMaxEndDate } = useAuth();
    const explorerDates = useExplorerDates();
    const clipsIa = useExplorerClipsIa();
    const currentMinDate = getDateFilterMinStartDate();
    const currentMaxDate = getDateFilterMaxEndDate();
    const { startDate, endDate, minDate: periodMin, maxDate: periodMax } = clipsExplorerPeriod(
        clipsIa,
        explorerDates.startDate,
        explorerDates.endDate,
        currentMinDate,
        currentMaxDate,
    );
    const setRange = explorerDates.setRange;
    const setSocialViewMode = useExplorerStore((s) => s.setSocialViewMode);
    const platform = useExplorerStore((s) => s.socialRanking.platform);
    const channelType = useExplorerStore((s) => s.socialRanking.channelType);
    const placementType = useExplorerStore((s) => s.socialSponsors.placementType);
    const setSocialSponsors = useExplorerStore((s) => s.setSocialSponsors);
    const [sortColumn, setSortColumn] = useState<BrandClipSortColumn>('publishedAt');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [platformFilter, setPlatformFilter] = useState<SocialPlatform | 'all'>(
        platform === 'all' ? 'all' : platform,
    );

    const handleSort = (column: BrandClipSortColumn) => {
        if (sortColumn === column) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortDirection(column === 'title' || column === 'channel' ? 'asc' : 'desc');
        }
        setPage(0);
    };

    const { data, isFetching, isPlaceholderData, error } = useQuery({
        queryKey: [
            'explorer-social-brand-clips',
            brandId,
            startDate,
            endDate,
            platformFilter,
            channelType,
            placementType,
            sortColumn,
            sortDirection,
            page,
            rowsPerPage,
        ],
        queryFn: ({ signal }) =>
            apiService.getExplorerSocialBrandClips(brandId!, startDate, endDate, {
                platform: platformFilter === 'all' ? undefined : platformFilter,
                channelType,
                placementType: placementType === 'all' ? undefined : placementType,
                sortBy: sortColumn,
                sortOrder: sortDirection,
                page: page + 1,
                limit: rowsPerPage,
                signal,
            }),
        enabled: !!brandId,
        staleTime: 30_000,
        gcTime: GC_TIME,
        placeholderData: keepPreviousData,
    });

    const brand = data?.brand;
    const showSkeleton = isFetching && !data;
    const showRefetching = isFetching && isPlaceholderData;
    const total = data?.pagination.total ?? brand?.clips ?? 0;

    const goBack = () => {
        if (clipsIa) {
            navigate(ROUTES_NAMES.EXPLORER_BRANDS_CLIPS);
            return;
        }
        setSocialViewMode('sponsors');
        navigate(ROUTES_NAMES.EXPLORER_SOCIAL_LIST);
    };

    return (
        <Container maxWidth="xl" sx={{ pb: 4 }}>
            <ExplorerHeader
                loading={showSkeleton}
                onBack={goBack}
                image={{
                    name: brand?.name ?? '',
                    src: brand?.s3Key ? ensureDirectS3Url(getS3Url(brand.s3Key)) : undefined,
                }}
                title={brand?.name ?? ''}
            >
                <DayRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onChange={setRange}
                    minDate={periodMin ?? currentMinDate}
                    maxDate={periodMax ?? currentMaxDate}
                />
            </ExplorerHeader>

            {error ? (
                <Alert severity="error">{t('loadSocialBrandError')}</Alert>
            ) : (
                <>
                    <Box sx={{ mb: 4 }}>
                        <SummarySection
                            title={t('periodSummary')}
                            isLoading={showSkeleton}
                            valueColor={theme.palette.text.primary}
                            cards={[
                                {
                                    title: t('clips'),
                                    value: formatNumber(brand?.clips ?? 0),
                                    tooltip: t('tooltip.clipCount'),
                                },
                                {
                                    title: t('channels'),
                                    value: formatNumber(brand?.channels ?? 0),
                                    tooltip: t('tooltip.socialSponsorChannels'),
                                },
                                {
                                    title: t('views'),
                                    value: formatNumber(brand?.views ?? 0),
                                    tooltip: t('tooltip.periodViews'),
                                },
                                {
                                    title: t('viewsPerClip'),
                                    value:
                                        brand?.viewsPerClip == null
                                            ? ''
                                            : formatNumber(brand.viewsPerClip),
                                    tooltip: t('tooltip.viewsPerClip'),
                                },
                                {
                                    title: t('clipInteractions'),
                                    value: formatNumber(brand?.interactions ?? 0),
                                    tooltip: t('tooltip.clipInteractions'),
                                },
                            ]}
                        />
                    </Box>

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mb: 2 }}
                    >
                        <Typography variant="h2" color="text.primary">
                            {t('recentClips', { count: brand?.clips ?? 0 })}
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
                            <FormControl size="small" sx={explorerFilterControlSx()}>
                                <InputLabel>{t('socialPlatform')}</InputLabel>
                                <Select
                                    value={platformFilter}
                                    label={t('socialPlatform')}
                                    renderValue={(value) =>
                                        value === 'all' ? (
                                            t('socialAllPlatforms')
                                        ) : (
                                            <PlatformName platform={value} />
                                        )
                                    }
                                    onChange={(e) => {
                                        setPlatformFilter(e.target.value as SocialPlatform | 'all');
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="all">{t('socialAllPlatforms')}</MenuItem>
                                    {SOCIAL_PLATFORMS.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            <PlatformName platform={item} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <ExplorerSocialPlacementSelect
                                value={placementType}
                                onChange={(next) => {
                                    setSocialSponsors({ placementType: next, page: 0 });
                                    setPage(0);
                                }}
                            />
                        </Stack>
                    </Stack>

                    <TableContainer
                        component={Paper}
                        sx={{
                            mb: 2,
                            position: 'relative',
                            width: '100%',
                            overflowX: 'hidden',
                            ...(showRefetching ? { opacity: 0.55, pointerEvents: 'none' } : {}),
                        }}
                    >
                        {showRefetching && (
                            <LinearProgress
                                sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}
                            />
                        )}
                        <Table aria-busy={showSkeleton || showRefetching} sx={CLIPS_TABLE_SX}>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sortDirection={sortColumn === 'channel' ? sortDirection : false}
                                    >
                                        <TableSortLabel
                                            active={sortColumn === 'channel'}
                                            direction={sortColumn === 'channel' ? sortDirection : 'asc'}
                                            onClick={() => handleSort('channel')}
                                        >
                                            <ExplorerDetailTableHeaderLabel
                                                label={t('channel')}
                                                tooltip={t('tooltip.socialChannelName')}
                                            />
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <ExplorerDetailTableHeaderLabel
                                            label={t('socialPlatform')}
                                            tooltip={t('tooltip.clipPlatform')}
                                        />
                                    </TableCell>
                                    <TableCell
                                        sortDirection={
                                            sortColumn === 'publishedAt' ? sortDirection : false
                                        }
                                    >
                                        <TableSortLabel
                                            active={sortColumn === 'publishedAt'}
                                            direction={
                                                sortColumn === 'publishedAt' ? sortDirection : 'desc'
                                            }
                                            onClick={() => handleSort('publishedAt')}
                                        >
                                            <ExplorerDetailTableHeaderLabel
                                                label={t('date')}
                                                tooltip={t('tooltip.clipPublishedAt')}
                                            />
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        sortDirection={sortColumn === 'title' ? sortDirection : false}
                                    >
                                        <TableSortLabel
                                            active={sortColumn === 'title'}
                                            direction={sortColumn === 'title' ? sortDirection : 'asc'}
                                            onClick={() => handleSort('title')}
                                        >
                                            <ExplorerDetailTableHeaderLabel
                                                label={t('title')}
                                                tooltip={t('tooltip.clipTitle')}
                                            />
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <ExplorerDetailTableHeaderLabel
                                            label={t('detection')}
                                            tooltip={t('tooltip.socialBrandDetection')}
                                        />
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sortDirection={sortColumn === 'views' ? sortDirection : false}
                                    >
                                        <TableSortLabel
                                            active={sortColumn === 'views'}
                                            direction={sortColumn === 'views' ? sortDirection : 'desc'}
                                            onClick={() => handleSort('views')}
                                            sx={RIGHT_TABLE_SORT_LABEL_SX}
                                        >
                                            <ExplorerDetailTableHeaderLabel
                                                label={t('views')}
                                                tooltip={t('tooltip.clipViews')}
                                                align="right"
                                            />
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sortDirection={
                                            sortColumn === 'interactions' ? sortDirection : false
                                        }
                                    >
                                        <TableSortLabel
                                            active={sortColumn === 'interactions'}
                                            direction={
                                                sortColumn === 'interactions' ? sortDirection : 'desc'
                                            }
                                            onClick={() => handleSort('interactions')}
                                            sx={RIGHT_TABLE_SORT_LABEL_SX}
                                        >
                                            <ExplorerDetailTableHeaderLabel
                                                label={t('interactions')}
                                                tooltip={t('tooltip.interactionsSum')}
                                                align="right"
                                            />
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {showSkeleton
                                    ? Array.from({ length: 5 }).map((_, index) => (
                                          <SocialBrandClipRowSkeleton key={`clip-skeleton-${index}`} />
                                      ))
                                    : data?.clips.length === 0
                                      ? (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center">
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ py: 4 }}
                                                    >
                                                        {t('noSocialBrandClips')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                      : data?.clips.map((clip) => (
                                            <TableRow
                                                key={clip._id}
                                                hover
                                                onClick={() => {
                                                    if (!clip.url) return;
                                                    window.open(clip.url, '_blank', 'noopener,noreferrer');
                                                }}
                                                sx={{ cursor: clip.url ? 'pointer' : 'default' }}
                                            >
                                                <TableCell>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1.5}
                                                        alignItems="center"
                                                        onClick={(event) => {
                                                            if (!clip.channelId) return;
                                                            event.stopPropagation();
                                                            navigate(explorerSocialChannelPath(clip.channelId, clipsIa));
                                                        }}
                                                        sx={{
                                                            minWidth: 0,
                                                            cursor: clip.channelId ? 'pointer' : 'default',
                                                        }}
                                                    >
                                                        <EntityImage
                                                            name={clip.channelName || '—'}
                                                            src={
                                                                clip.channelImageS3Key
                                                                    ? ensureDirectS3Url(
                                                                          getS3Url(clip.channelImageS3Key),
                                                                      )
                                                                    : undefined
                                                            }
                                                            size={32}
                                                            variant="plain"
                                                        />
                                                        <Typography variant="body2" fontWeight={600} noWrap>
                                                            {clip.channelName || '—'}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <PlatformName
                                                        platform={clip.platform as ExplorerSocialPlatform}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.25}>
                                                        <Typography variant="body2" color="text.primary">
                                                            {formatExplorerTableDateLine(clip.publishedAt)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {formatExplorerTableTimeLine(clip.publishedAt)}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight={600}
                                                        noWrap
                                                        title={clip.title || undefined}
                                                        sx={{
                                                            display: 'block',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {clip.title || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {clip.placementType ? (
                                                        <Tooltip
                                                            title={clip.detection || t(`socialPlacement.${clip.placementType}`)}
                                                            arrow
                                                            placement="top"
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                color="text.primary"
                                                                noWrap
                                                                sx={{
                                                                    display: 'block',
                                                                    minWidth: 0,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                {t(`socialPlacement.${clip.placementType}`)}
                                                            </Typography>
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            —
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {clip.views > 0 ? formatNumber(clip.views) : '—'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNumber(clip.interactions)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {clip.url ? (
                                                        <IconButton
                                                            size="small"
                                                            component="a"
                                                            href={clip.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(event) => event.stopPropagation()}
                                                            aria-label={t('openClip')}
                                                        >
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        labelRowsPerPage={t('rowsPerPage')}
                        labelDisplayedRows={({ from, to, count }) =>
                            explorerPaginationLabel(from, to, count)
                        }
                    />
                </>
            )}
        </Container>
    );
};

export default ExplorerSocialBrandDetail;
